import React, { useState, useRef, useEffect } from 'react';
import { View } from 'react-native-web';
import CustomText from '../atoms/CustomText';
import { borderRadius, colors, spacing, typography } from '../../utils/styling';
import { useLocation, useNavigate } from 'react-router-dom';
import { useResponsive } from '../../hooks/useResponsive';

const progressMessages = [
  { threshold: 0, message: "Analyzing your responses" },
  { threshold: 25, message: "Calculating personality traits" },
  { threshold: 50, message: "Processing your results" },
  { threshold: 75, message: "Generating insights" },
  { threshold: 95, message: "Finalizing your profile" }
];

const ellipsisStates = ['', '.', '..', '...', '....'];

interface ProcessingResultsProps {
  answers: number[];
  onReset: () => void;
  clientId?: string;
}

const ProcessingResults: React.FC<ProcessingResultsProps> = ({ answers, onReset, clientId }) => {
  const { isMobile } = useResponsive();
  const [visualProgress, setVisualProgress] = useState(0);
  const [currentProgressMessage, setCurrentProgressMessage] = useState(progressMessages[0].message);
  const [ellipsisIndex, setEllipsisIndex] = useState(0);
  const [isIncrementing, setIsIncrementing] = useState(true);
  const animationInterval = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Progress animation
  useEffect(() => {
    if (animationInterval.current) {
      clearInterval(animationInterval.current);
    }
    const totalTime = 6000; // 6 seconds for processing
    const intervalTime = 60;
    let elapsedTime = 0;
    animationInterval.current = setInterval(() => {
      elapsedTime += intervalTime;
      const progress = Math.min(100, (elapsedTime / totalTime) * 100);
      setVisualProgress(progress);
      // Update progress message
      for (let i = progressMessages.length - 1; i >= 0; i--) {
        if (progress >= progressMessages[i].threshold) {
          setCurrentProgressMessage(progressMessages[i].message);
          break;
        }
      }
      if (progress >= 100) {
        clearInterval(animationInterval.current!);
        setTimeout(() => {
          if (location.pathname === "/quiz/specific") {
            // We're on the specific quiz page - calculate specific results
          //   navigate('/quiz/specific/results', {
          //     state: {
          //       answers,
          //       clientId,
          //       mainResultsData: location.state,
          //       from: '/quiz/specific'
          //     }
          //   });
          // } else {
          //   // We're on the main quiz page - calculate main results
          //   navigate('/quiz/results/submit', {
          //     state: {
          //       answers,
          //       clientId,
          //       from: '/quiz'
          //     },
          //   });
          }
        }, 400); // slight delay for smoothness
      }
    }, intervalTime);
    return () => {
      if (animationInterval.current) {
        clearInterval(animationInterval.current);
      }
    };
  }, [navigate, answers, location.pathname, clientId, location.state]);

  // Ellipsis animation
  useEffect(() => {
    const interval = setInterval(() => {
      setEllipsisIndex((prevIndex) => {
        if (isIncrementing && prevIndex === ellipsisStates.length - 1) {
          setIsIncrementing(false);
          return prevIndex - 1;
        } else if (!isIncrementing && prevIndex === 0) {
          setIsIncrementing(true);
          return prevIndex + 1;
        }
        return isIncrementing ? prevIndex + 1 : prevIndex - 1;
      });
    }, 400);
    return () => clearInterval(interval);
  }, [isIncrementing]);

  return (
    <View style={{
      marginTop: isMobile ? spacing.xl : spacing.xxl,
      marginHorizontal: spacing.xl,
      borderRadius: borderRadius.xl,
      flex: 1,
      padding: isMobile ? spacing.lg : spacing.xl,
      backgroundColor: colors.white,
      alignItems: 'center',
      justifyContent: 'center',
      width: '90%',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
      overflowX: 'hidden',
      border: '1px solid black'
    }}>
      <View style={{ 
        flex: 1, 
        alignItems: 'center', 
        justifyContent: 'center', 
        width: '100%',
        marginBottom: isMobile ? spacing.md : spacing.lg,
      }}>
        <CustomText style={{ 
          fontSize: 72, 
          fontWeight: '700', 
          color: colors.dark, 
          lineHeight: 80, 
          marginBottom: spacing.xl, 
          fontFamily: "Nunito" 
        }}>
          {Math.floor(visualProgress)}%
        </CustomText>
        <View style={{ width: '100%', maxWidth: 400, margin: '0 auto', marginBottom: spacing.xl }}>
          <View style={{ width: '100%', height: 8, backgroundColor: colors.gray[200], borderRadius: 4 }}>
            <View
              style={{
                width: `${visualProgress}%`,
                height: '100%',
                backgroundColor: colors.primary,
                borderRadius: 4,
                transition: 'width 0.3s',
              }}
            />
          </View>
        </View>
        <View style={{ marginBottom: spacing.xl }}>
          <CustomText style={{ 
            fontSize: 32, 
            fontWeight: '600', 
            color: colors.dark, 
            textAlign: 'center', 
            marginBottom: spacing.sm, 
            fontFamily: "Nunito",
          }}>
            We're analyzing
          </CustomText>
          <CustomText style={{ 
            fontSize: 32, 
            fontWeight: '600', 
            color: colors.dark, 
            textAlign: 'center', 
            fontFamily: "Nunito",
          }}>
            your personality
          </CustomText>
        </View>
        <CustomText style={{ 
          fontSize: 18, 
          color: colors.gray[600], 
          textAlign: 'center', 
          fontFamily: "Nunito",
        }}>
          {currentProgressMessage}{ellipsisStates[ellipsisIndex]}
        </CustomText>
      </View>
      {/* Reviews section */}
      <View style={{
        duration: '2s',
        width: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
      >
        {/* Content */}
        <View style={{
          backgroundColor: '#fafafa',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-evenly',
          alignItems: 'center',
          width: '100%',
          borderRadius: borderRadius.xl,
          padding: isMobile ? spacing.md : spacing.lg,
          position: 'relative',
          duration: '2s',
        }}>
          <CustomText style={{ 
            color: '#fec000', 
            fontSize: 22,
            marginRight: 1,
            textAlign: 'center',
          }}>
            ★ ★ ★ ★ ★
          </CustomText>
          <CustomText style={{ 
            color: 'black', 
            fontSize: isMobile ? 18 : 24, 
            marginHorizontal: 1,
            fontFamily: 'Nunito',
            fontWeight: 500,
            textAlign: 'center',
          }}>
            93% of people immediately learned something new from The Money Personality&trade;
          </CustomText>
          <CustomText style={{ 
            color: '#fec000', 
            fontSize: 22,
            marginLeft: 1,
            textAlign: 'center',
          }}>
            ★ ★ ★ ★ ★
          </CustomText>
        </View>
        {/* Pulsating border */}
        <div className="pulsate-border"/>
      </View>
    </View>
  );
};

export default ProcessingResults; 